/**
 * Event Dispatcher for Secure EmployeeTalk State Management
 *
 * Handles WebSocket communication and event dispatching for the ETT system.
 * All events are sent to the backend for validation and processing before
 * state updates are broadcast to all connected clients.
 *
 * Features:
 * - Type-safe event dispatching with shared interfaces
 * - Automatic WebSocket subscription management
 * - Backend validation of all state changes
 * - Real-time state synchronization across clients
 */

import { EWebsocketTopics } from '@sharedInterfaces/globalEnums';
import type { EGender } from '@sharedInterfaces/IEmployee';
import type {
    IEmployeeTalkEvent,
    IEmployeeTalkWebSocketMessage,
    INavigateToStepEvent,
    IRegenerateDescriptionEvent,
    IRegenerateSkillsEvent,
    IRequestStateEvent,
    IUpdateComponentDataEvent,
    IUpdateDescriptionEvent,
    IUpdatePersonalDataEvent,
} from '@sharedInterfaces/IEmployeeTalkEvents';
import { WebSocketClient } from '@src/APIs/WebSockets/WebSocketClient';

export class EmployeeTalkEventDispatcher {
    private employeeTalkId: number;
    private subscriptionKey: string | null = null;

    constructor(employeeTalkId: number) {
        this.employeeTalkId = employeeTalkId;
    }

    /**
     * Subscribe to WebSocket messages for this EmployeeTalk
     * @param handler - Function to handle incoming WebSocket messages
     */
    subscribe(handler: (message: IEmployeeTalkWebSocketMessage) => void): void {
        this.subscriptionKey = WebSocketClient.subscribe(EWebsocketTopics.EMPLOYEE_TALK_EVENT, (data: unknown) => {
            handler(data as IEmployeeTalkWebSocketMessage);
        });
    }

    // Unsubscribe from WebSocket messages
    unsubscribe(): void {
        if (this.subscriptionKey) {
            WebSocketClient.unsubscribe(this.subscriptionKey);
            this.subscriptionKey = null;
        }
    }

    // Request current state from backend
    async requestState(): Promise<void> {
        const event: IRequestStateEvent = {
            type: 'REQUEST_STATE',
            timestamp: new Date().toISOString(),
            employeeTalkId: this.employeeTalkId.toString(),
            templateId: '', // Will be filled by backend
            payload: {},
        };
        await this.dispatchEvent(event);
    }

    // Navigate to specific step
    async navigateToStep(step: number): Promise<void> {
        const event: INavigateToStepEvent = {
            type: 'NAVIGATE_TO_STEP',
            timestamp: new Date().toISOString(),
            employeeTalkId: this.employeeTalkId.toString(),
            templateId: '', // Will be filled by backend
            payload: { step },
        };
        await this.dispatchEvent(event);
    }

    // Update component data
    async updateComponentData(componentId: string, data: unknown): Promise<void> {
        const event: IUpdateComponentDataEvent = {
            type: 'UPDATE_COMPONENT_DATA',
            timestamp: new Date().toISOString(),
            employeeTalkId: this.employeeTalkId.toString(),
            templateId: '', // Will be filled by backend
            payload: { componentId, data },
        };
        await this.dispatchEvent(event);
    }

    // Update personal data (only changed fields) - WebSocket-only
    async updatePersonalData(data: {
        firstName?: string;
        lastName?: string;
        gender?: EGender;
        phoneNumber?: string;
    }): Promise<void> {
        const event: IUpdatePersonalDataEvent = {
            type: 'UPDATE_PERSONAL_DATA',
            timestamp: new Date().toISOString(),
            employeeTalkId: this.employeeTalkId.toString(),
            templateId: '', // Will be filled by backend
            payload: data,
        };
        await this.dispatchEvent(event);
    }

    // Update description - WebSocket-only
    async updateDescription(description: string): Promise<void> {
        const event: IUpdateDescriptionEvent = {
            type: 'UPDATE_DESCRIPTION',
            timestamp: new Date().toISOString(),
            employeeTalkId: this.employeeTalkId.toString(),
            templateId: '', // Will be filled by backend
            payload: { description },
        };
        await this.dispatchEvent(event);
    }

    // Regenerate personal data - WebSocket-only
    async regeneratePersonalData(): Promise<void> {
        const event: import('@sharedInterfaces/IEmployeeTalkEvents').IRegeneratePersonalDataEvent = {
            type: 'REGENERATE_PERSONAL_DATA',
            timestamp: new Date().toISOString(),
            employeeTalkId: this.employeeTalkId.toString(),
            templateId: '',
            payload: {},
        };
        await this.dispatchEvent(event);
    }

    // Regenerate description - WebSocket-only
    async regenerateDescription(): Promise<void> {
        const event: IRegenerateDescriptionEvent = {
            type: 'REGENERATE_DESCRIPTION',
            timestamp: new Date().toISOString(),
            employeeTalkId: this.employeeTalkId.toString(),
            templateId: '', // Will be filled by backend
            payload: {},
        };
        await this.dispatchEvent(event);
    }

    // Regenerate skills - WebSocket-only
    async regenerateSkills(): Promise<void> {
        const event: IRegenerateSkillsEvent = {
            type: 'REGENERATE_SKILLS',
            timestamp: new Date().toISOString(),
            employeeTalkId: this.employeeTalkId.toString(),
            templateId: '', // Will be filled by backend
            payload: {},
        };
        await this.dispatchEvent(event);
    }

    // Regenerate certificates - WebSocket-only
    async regenerateCertificates(): Promise<void> {
        const event: import('@sharedInterfaces/IEmployeeTalkEvents').IRegenerateCertificatesEvent = {
            type: 'REGENERATE_CERTIFICATES',
            timestamp: new Date().toISOString(),
            employeeTalkId: this.employeeTalkId.toString(),
            templateId: '',
            payload: {},
        };
        await this.dispatchEvent(event);
    }

    // Regenerate roles - WebSocket-only
    async regenerateRoles(): Promise<void> {
        const event: import('@sharedInterfaces/IEmployeeTalkEvents').IRegenerateRolesEvent = {
            type: 'REGENERATE_ROLES',
            timestamp: new Date().toISOString(),
            employeeTalkId: this.employeeTalkId.toString(),
            templateId: '',
            payload: {},
        };
        await this.dispatchEvent(event);
    }

    // Selection Events
    async updateSkillLevel(skill: string, level: number): Promise<void> {
        const event: import('@sharedInterfaces/IEmployeeTalkEvents').IUpdateSkillLevelEvent = {
            type: 'UPDATE_SKILL_LEVEL',
            timestamp: new Date().toISOString(),
            employeeTalkId: this.employeeTalkId.toString(),
            templateId: '',
            payload: { skill, level },
        };
        await this.dispatchEvent(event);
    }

    async selectSkill(skill: string, level?: number): Promise<void> {
        const event: import('@sharedInterfaces/IEmployeeTalkEvents').ISelectSkillEvent = {
            type: 'SELECT_SKILL',
            timestamp: new Date().toISOString(),
            employeeTalkId: this.employeeTalkId.toString(),
            templateId: '',
            payload: level !== undefined ? { skill, level } : { skill },
        };
        await this.dispatchEvent(event);
    }

    async deselectSkill(skill: string): Promise<void> {
        const event: import('@sharedInterfaces/IEmployeeTalkEvents').IDeselectSkillEvent = {
            type: 'DESELECT_SKILL',
            timestamp: new Date().toISOString(),
            employeeTalkId: this.employeeTalkId.toString(),
            templateId: '',
            payload: { skill },
        };
        await this.dispatchEvent(event);
    }

    async selectCertificate(
        certificate: string,
        startDate?: Date,
        endDate?: Date,
        durationInDays?: number,
    ): Promise<void> {
        const event: import('@sharedInterfaces/IEmployeeTalkEvents').ISelectCertificateEvent = {
            type: 'SELECT_CERTIFICATE',
            timestamp: new Date().toISOString(),
            employeeTalkId: this.employeeTalkId.toString(),
            templateId: '',
            payload: {
                certificate,
                startDate: startDate?.toISOString(),
                endDate: endDate?.toISOString(),
                durationInDays,
            },
        };
        await this.dispatchEvent(event);
    }

    async deselectCertificate(certificate: string): Promise<void> {
        const event: import('@sharedInterfaces/IEmployeeTalkEvents').IDeselectCertificateEvent = {
            type: 'DESELECT_CERTIFICATE',
            timestamp: new Date().toISOString(),
            employeeTalkId: this.employeeTalkId.toString(),
            templateId: '',
            payload: { certificate },
        };
        await this.dispatchEvent(event);
    }

    async selectRole(role: string): Promise<void> {
        const event: import('@sharedInterfaces/IEmployeeTalkEvents').ISelectRoleEvent = {
            type: 'SELECT_ROLE',
            timestamp: new Date().toISOString(),
            employeeTalkId: this.employeeTalkId.toString(),
            templateId: '',
            payload: { role },
        };
        await this.dispatchEvent(event);
    }

    async deselectRole(role: string): Promise<void> {
        const event: import('@sharedInterfaces/IEmployeeTalkEvents').IDeselectRoleEvent = {
            type: 'DESELECT_ROLE',
            timestamp: new Date().toISOString(),
            employeeTalkId: this.employeeTalkId.toString(),
            templateId: '',
            payload: { role },
        };
        await this.dispatchEvent(event);
    }

    async selectSuperPower(superPower: string): Promise<void> {
        const event: import('@sharedInterfaces/IEmployeeTalkEvents').ISelectSuperPowerEvent = {
            type: 'SELECT_SUPER_POWER',
            timestamp: new Date().toISOString(),
            employeeTalkId: this.employeeTalkId.toString(),
            templateId: '',
            payload: { superPower },
        };
        await this.dispatchEvent(event);
    }

    async deselectSuperPower(superPower: string): Promise<void> {
        const event: import('@sharedInterfaces/IEmployeeTalkEvents').IDeselectSuperPowerEvent = {
            type: 'DESELECT_SUPER_POWER',
            timestamp: new Date().toISOString(),
            employeeTalkId: this.employeeTalkId.toString(),
            templateId: '',
            payload: { superPower },
        };
        await this.dispatchEvent(event);
    }

    async setManualMode(manualMode: boolean): Promise<void> {
        const event: import('@sharedInterfaces/IEmployeeTalkEvents').ISetManualModeEvent = {
            type: 'SET_MANUAL_MODE',
            timestamp: new Date().toISOString(),
            employeeTalkId: this.employeeTalkId.toString(),
            templateId: '',
            payload: { manualMode },
        };
        await this.dispatchEvent(event);
    }

    async ignoreChanges(componentId: string, changeType: 'personal' | 'description'): Promise<void> {
        const event: import('@sharedInterfaces/IEmployeeTalkEvents').IIgnoreChangesEvent = {
            type: 'IGNORE_CHANGES',
            timestamp: new Date().toISOString(),
            employeeTalkId: this.employeeTalkId.toString(),
            templateId: '',
            payload: { componentId, changeType },
        };
        await this.dispatchEvent(event);
    }

    async approveEmployeeTalk(role: 'employee' | 'manager'): Promise<void> {
        const event: import('@sharedInterfaces/IEmployeeTalkEvents').IApproveEmployeeTalkEvent = {
            type: 'APPROVE_EMPLOYEE_TALK',
            timestamp: new Date().toISOString(),
            employeeTalkId: this.employeeTalkId.toString(),
            templateId: '',
            payload: { role },
        };
        await this.dispatchEvent(event);
    }

    async unapproveEmployeeTalk(role: 'employee' | 'manager'): Promise<void> {
        const event: import('@sharedInterfaces/IEmployeeTalkEvents').IUnapproveEmployeeTalkEvent = {
            type: 'UNAPPROVE_EMPLOYEE_TALK',
            timestamp: new Date().toISOString(),
            employeeTalkId: this.employeeTalkId.toString(),
            templateId: '',
            payload: { role },
        };
        await this.dispatchEvent(event);
    }

    /**
     * Dispatch event to backend for validation and processing
     * Backend will validate the event and broadcast state updates to all clients
     */
    private async dispatchEvent(event: IEmployeeTalkEvent): Promise<void> {
        await WebSocketClient.sendMessage(EWebsocketTopics.EMPLOYEE_TALK_EVENT, event);
    }
}
